import React, { useRef, useState } from "react";
import { API_BASE_URL } from "../services/api";

/**
 * Play/pause button for a lesson's narration. On first play it points a hidden
 * <audio> element at the lesson's public GCS URL (audioUrl) and plays it,
 * falling back to the backend's same-origin streaming endpoint.
 */
export default function AudioButton({ slug, hasAudio, audioUrl }) {
  const audioRef = useRef(null);
  const [state, setState] = useState("idle"); // idle | loading | playing | paused | error
  const [error, setError] = useState("");

  if (!hasAudio) {
    return (
      <span className="inline-flex items-center rounded-lg bg-slate-900/60 px-4 py-2 text-sm text-slate-500">
        No audio yet
      </span>
    );
  }

  const ensureSrc = () => {
    const el = audioRef.current;
    if (el.getAttribute("src")) return;
    setState("loading");
    // Play from the GCS public URL when available; else the backend endpoint.
    el.setAttribute("src", audioUrl || `${API_BASE_URL}/api/lessons/${slug}/audio`);
  };

  const toggle = async () => {
    const el = audioRef.current;
    try {
      if (state === "playing") {
        el.pause();
        return;
      }
      ensureSrc();
      await el.play();
    } catch (err) {
      setError(err?.response?.data?.detail || err.message || "playback failed");
      setState("error");
    }
  };

  const label = {
    idle: "▶  Play audio",
    loading: "…  Loading",
    playing: "❚❚  Pause",
    paused: "▶  Resume",
    error: "▶  Retry",
  }[state];

  return (
    <div className="flex items-center gap-3">
      <button
        onClick={toggle}
        disabled={state === "loading"}
        className="inline-flex items-center rounded-lg bg-sky-600 px-4 py-2 text-sm font-medium text-white transition hover:bg-sky-500 disabled:opacity-60"
      >
        {label}
      </button>
      {state === "error" && (
        <span className="text-sm text-rose-400">{error}</span>
      )}
      <audio
        ref={audioRef}
        preload="none"
        onPlaying={() => setState("playing")}
        onPause={() =>
          setState((s) => (s === "playing" ? "paused" : s))
        }
        onEnded={() => setState("idle")}
      />
    </div>
  );
}
